import { SkeletonText } from '@dev-spendesk/grapes';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useUser } from 'modules/app/hooks/useUser';
import { useRawCustomFieldsQuery } from 'modules/custom-fields/hooks/useRawCustomFieldsQuery';
import { QueryError } from 'src/core/common/components/QueryError';
import { getEligibleCustomFields } from 'src/core/utils/custom-fields';

import { CustomFieldFilter } from './CustomFieldFilter';
import { type CustomFieldFiltersType } from './types';

type Props = {
  eligibleTypes: string[];
  filters: CustomFieldFiltersType;
  setFilters: (filters: CustomFieldFiltersType) => void;
  disableSelectUndefined?: boolean;
};

export const CustomFieldFilters = ({
  eligibleTypes,
  filters,
  setFilters,
  disableSelectUndefined,
}: Props) => {
  const companyId = useCompanyId();
  const user = useUser();

  const customFieldsQueryState = useRawCustomFieldsQuery();

  const teamIds = user.data_by_company[companyId].groups_ids ?? [];

  return (
    <QuerySuspense
      queryState={customFieldsQueryState}
      loading={
        <div className="mb-24">
          <SkeletonText width="40%" />
          <SkeletonText size="xl" />
        </div>
      }
      fallback={(error) => (
        <QueryError
          queryError={error}
          componentType="Callout"
          logContext={{
            team: 'finance-controller',
          }}
          translations={{
            serverError: 'filters.customFields.errors.fetchCustomFields',
          }}
        />
      )}
    >
      {(customFields) => {
        const eligibleCustomFields = getEligibleCustomFields(customFields, {
          types: eligibleTypes,
          all: true,
          teamIds,
          user,
        });
        return (
          <>
            {eligibleCustomFields.map((customField, index) => (
              <CustomFieldFilter
                key={customField.id}
                filters={filters}
                customField={customField}
                setFilters={setFilters}
                disableSelectUndefined={disableSelectUndefined}
                className={
                  index === eligibleCustomFields.length - 1 ? '' : 'mb-24'
                }
              />
            ))}
          </>
        );
      }}
    </QuerySuspense>
  );
};
